(() => {
  const SECTION_SELECTOR = '[data-cfaq-section]';
  const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
  const ITEM_SPACING = '1.6rem';
  const STOP_WORDS = new Set([
    'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'can', 'do', 'does', 'for', 'from',
    'how', 'i', 'in', 'is', 'it', 'of', 'on', 'or', 'that', 'the', 'to', 'what', 'when',
    'where', 'which', 'who', 'why', 'with', 'you', 'your'
  ]);

  const normalize = (value) => {
    if (!value) return '';

    return value
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const unique = (values) => [...new Set(values.filter(Boolean))];
  const pluralize = (count, singular, plural) => (count === 1 ? singular : plural);
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const getTokens = (query) => {
    const normalized = normalize(query);
    const rawTokens = normalized ? normalized.split(' ') : [];
    let tokens = rawTokens.filter((token) => token.length > 1 && !STOP_WORDS.has(token));

    if (!tokens.length) {
      tokens = rawTokens;
    }

    return unique(tokens);
  };

  const buildQuery = (rawQuery) => {
    const phrase = normalize(rawQuery);
    const tokens = getTokens(rawQuery);
    const highlightTerms = unique([
      rawQuery.trim().replace(/\s+/g, ' '),
      ...tokens,
    ])
      .filter((term) => term.length > 1)
      .sort((left, right) => right.length - left.length);

    return {
      raw: rawQuery || '',
      phrase,
      tokens,
      highlightTerms,
    };
  };

  const containsWholeWord = (text, token) => {
    if (!text || !token) return false;

    const matcher = new RegExp(`(^|\\s)${escapeRegExp(token)}(?=\\s|$)`);
    return matcher.test(text);
  };

  const scoreEntry = (entry, query) => {
    if (!query.phrase) {
      return {
        matches: true,
        score: 0,
      };
    }

    const question = entry.search.question;
    const answer = entry.search.answer;
    const keywords = entry.search.keywords;

    let score = 0;
    let matchedTokens = 0;
    let missingTokens = 0;
    let phraseMatch = false;

    if (query.phrase.length > 1) {
      if (question.includes(query.phrase)) {
        score += 220;
        phraseMatch = true;
      }

      if (question.startsWith(query.phrase)) {
        score += 80;
      }

      if (keywords.includes(query.phrase)) {
        score += 160;
        phraseMatch = true;
      }

      if (answer.includes(query.phrase)) {
        score += 110;
        phraseMatch = true;
      }
    }

    query.tokens.forEach((token) => {
      let tokenMatched = false;

      if (question.includes(token)) {
        score += 70;
        tokenMatched = true;

        if (containsWholeWord(question, token)) {
          score += 18;
        }
      }

      if (keywords.includes(token)) {
        score += 52;
        tokenMatched = true;

        if (containsWholeWord(keywords, token)) {
          score += 14;
        }
      }

      if (answer.includes(token)) {
        score += 28;
        tokenMatched = true;

        if (containsWholeWord(answer, token)) {
          score += 8;
        }
      }

      if (tokenMatched) {
        matchedTokens += 1;
      } else {
        missingTokens += 1;
      }
    });

    const tokenCount = query.tokens.length;
    const minMatches = tokenCount <= 2 ? tokenCount : Math.max(2, Math.ceil(tokenCount * 0.75));
    const matches = tokenCount === 0 ? phraseMatch : phraseMatch || matchedTokens >= minMatches;

    if (!matches) {
      return {
        matches: false,
        score: 0,
      };
    }

    score += matchedTokens * 18;
    score -= missingTokens * 15;
    score -= entry.order * 0.01;

    return {
      matches: true,
      score,
    };
  };

  const resetHighlights = (entry) => {
    if (entry.questionNode) {
      entry.questionNode.textContent = entry.questionText;
    }

    if (entry.answerNode) {
      entry.answerNode.innerHTML = entry.answerHtml;
    }
  };

  const highlightElement = (root, terms) => {
    if (!root || !terms.length) return;

    const pattern = terms.map((term) => escapeRegExp(term)).join('|');

    if (!pattern) return;

    const matcher = new RegExp(pattern, 'gi');
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue || !node.nodeValue.trim()) {
          return NodeFilter.FILTER_REJECT;
        }

        if (node.parentElement && node.parentElement.closest('mark')) {
          return NodeFilter.FILTER_REJECT;
        }

        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const nodes = [];

    while (walker.nextNode()) {
      nodes.push(walker.currentNode);
    }

    nodes.forEach((node) => {
      const text = node.nodeValue;
      matcher.lastIndex = 0;
      const matches = Array.from(text.matchAll(matcher));

      if (!matches.length || !node.parentNode) return;

      const fragment = document.createDocumentFragment();
      let lastIndex = 0;

      matches.forEach((match) => {
        const start = typeof match.index === 'number' ? match.index : -1;
        const value = match[0];

        if (start < 0 || start < lastIndex) return;

        if (start > lastIndex) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex, start)));
        }

        const mark = document.createElement('mark');
        mark.className = 'cremation-faq__mark';
        mark.textContent = value;
        fragment.appendChild(mark);
        lastIndex = start + value.length;
      });

      if (lastIndex < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
      }

      node.parentNode.replaceChild(fragment, node);
    });
  };

  const setInteractiveState = (entry, isVisible) => {
    if (isVisible) {
      entry.item.removeAttribute('aria-hidden');
      entry.item.removeAttribute('inert');
      return;
    }

    entry.item.setAttribute('aria-hidden', 'true');
    entry.item.setAttribute('inert', '');
  };

  const resetEntryState = (entry) => {
    entry.item.hidden = false;
    entry.item.classList.remove('is-matched', 'is-page-hidden', 'is-filter-hidden');
    entry.item.style.order = '';
    entry.item.style.marginTop = '';
    resetHighlights(entry);
    setInteractiveState(entry, true);
  };

  const applyVisibleSpacing = (visibleEntries) => {
    visibleEntries.forEach((entry, index) => {
      entry.item.style.marginTop = index === 0 ? '0' : ITEM_SPACING;
    });
  };

  const setPageButtonState = (button, page, activePage) => {
    const isActive = page === activePage;
    button.classList.toggle('is-active', isActive);

    if (isActive) {
      button.setAttribute('aria-current', 'page');
    } else {
      button.removeAttribute('aria-current');
    }
  };

  const renderPageButtons = (container, pageCount, activePage) => {
    if (!container) return;

    container.innerHTML = '';

    if (pageCount <= 1) {
      container.hidden = true;
      return;
    }

    const fragment = document.createDocumentFragment();

    for (let page = 1; page <= pageCount; page += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cremation-faq__page-button';
      button.dataset.page = String(page);
      button.textContent = String(page);
      button.setAttribute('aria-label', `Go to page ${page}`);
      setPageButtonState(button, page, activePage);
      fragment.appendChild(button);
    }

    container.appendChild(fragment);
    container.hidden = false;
  };

  const initSection = (section) => {
    if (!section || section.dataset.cfaqInitialized === 'true') return;

    section.dataset.cfaqInitialized = 'true';

    const form = section.querySelector('[data-cfaq-form]');
    const input = section.querySelector('[data-cfaq-input]');
    const clearButton = section.querySelector('[data-cfaq-clear]');
    const count = section.querySelector('[data-cfaq-count]');
    const emptyState = section.querySelector('[data-cfaq-empty]');
    const pagination = section.querySelector('[data-cfaq-pagination]');
    const pageButtons = section.querySelector('[data-cfaq-page-buttons]');
    const pageSizeSelect = section.querySelector('[data-cfaq-page-size-select]');
    const items = Array.from(section.querySelectorAll('[data-cfaq-item]'));

    if (!items.length) return;

    const entries = items.map((item, index) => {
      const questionNode = item.querySelector('[data-cfaq-question]');
      const answerNode = item.querySelector('[data-cfaq-answer]');
      const questionText = item.dataset.cfaqQuestionText || (questionNode ? questionNode.textContent.trim() : '');
      const answerText = item.dataset.cfaqAnswerText || (answerNode ? answerNode.textContent.trim() : '');
      const searchTerms = item.dataset.cfaqSearchTerms || '';
      const order = Number(item.dataset.cfaqOrder || index + 1);

      return {
        item,
        order,
        questionNode,
        answerNode,
        questionText,
        answerText,
        answerHtml: answerNode ? answerNode.innerHTML : '',
        search: {
          question: normalize(questionText),
          answer: normalize(answerText),
          keywords: normalize(searchTerms),
        },
      };
    });

    const total = entries.length;
    const defaultPageSize = Number(section.dataset.cfaqDefaultPageSize || 10);
    let pageSize = PAGE_SIZE_OPTIONS.includes(defaultPageSize) ? defaultPageSize : 10;
    let activePage = 1;
    let activeQuery = '';
    let renderFrame = 0;

    const findEntryByHash = () => {
      const hash = decodeURIComponent(window.location.hash.replace(/^#/, '')).trim();

      if (!hash) return null;

      return entries.find((entry) => entry.item.id === hash) || null;
    };

    const getPageFromHash = () => {
      const hash = decodeURIComponent(window.location.hash.replace(/^#/, '')).trim();

      if (!hash || !hash.startsWith('faq-page-')) return null;

      const match = hash.match(/-(\d+)$/);
      return match ? Number(match[1]) : null;
    };

    const updateCount = (visibleCount, isFiltering, pageCount) => {
      if (!count) return;

      if (isFiltering) {
        if (visibleCount === 0) {
          count.textContent = 'No matching questions';
          return;
        }

        count.textContent = `${visibleCount} matching ${pluralize(visibleCount, 'question', 'questions')}`;
        return;
      }

      const start = total === 0 ? 0 : ((activePage - 1) * pageSize) + 1;
      const end = Math.min(activePage * pageSize, total);

      if (pageCount <= 1 && pageSize >= total) {
        count.textContent = `${total} ${pluralize(total, 'question', 'questions')}`;
        return;
      }

      count.textContent = `Showing ${start}–${end} of ${total} ${pluralize(total, 'question', 'questions')}`;
    };

    const showPagination = (pageCount) => {
      if (!pagination) return;

      pagination.hidden = false;

      if (pageButtons) {
        pageButtons.hidden = pageCount <= 1;
      }
    };

    const hidePagination = () => {
      if (!pagination) return;

      pagination.hidden = true;
    };

    const renderPaginated = () => {
      const pageCount = Math.max(1, Math.ceil(total / pageSize));
      const firstVisibleIndex = (clamp(activePage, 1, pageCount) - 1) * pageSize;
      const lastVisibleIndex = firstVisibleIndex + pageSize;
      const visibleEntries = [];

      activePage = clamp(activePage, 1, pageCount);

      if (pageSizeSelect && pageSizeSelect.value !== String(pageSize)) {
        pageSizeSelect.value = String(pageSize);
      }

      entries.forEach((entry) => {
        resetEntryState(entry);
      });

      entries.forEach((entry, index) => {
        const isVisible = index >= firstVisibleIndex && index < lastVisibleIndex;

        if (isVisible) {
          visibleEntries.push(entry);
          return;
        }

        entry.item.classList.add('is-page-hidden');
        entry.item.style.marginTop = '0';
        setInteractiveState(entry, false);
      });

      applyVisibleSpacing(visibleEntries);
      renderPageButtons(pageButtons, pageCount, activePage);
      showPagination(pageCount);
      updateCount(visibleEntries.length, false, pageCount);

      if (emptyState) {
        emptyState.hidden = true;
      }

      if (clearButton) {
        clearButton.hidden = activeQuery.trim().length === 0;
      }
    };

    const renderFiltered = () => {
      const query = buildQuery(activeQuery);

      if (!query.phrase) {
        renderPaginated();
        return;
      }

      const matches = entries
        .map((entry) => {
          const result = scoreEntry(entry, query);

          return {
            entry,
            score: result.score,
            matches: result.matches,
          };
        })
        .filter((result) => result.matches)
        .sort((left, right) => {
          if (right.score === left.score) {
            return left.entry.order - right.entry.order;
          }

          return right.score - left.score;
        });

      const visibleEntries = [];

      entries.forEach((entry) => {
        resetEntryState(entry);
      });

      entries.forEach((entry) => {
        entry.item.classList.add('is-filter-hidden');
        entry.item.style.marginTop = '0';
        setInteractiveState(entry, false);
      });

      matches.forEach((match, index) => {
        const { entry } = match;

        entry.item.classList.remove('is-filter-hidden');
        entry.item.classList.add('is-matched');
        entry.item.style.order = String(index + 1);
        highlightElement(entry.questionNode, query.highlightTerms);
        highlightElement(entry.answerNode, query.highlightTerms);
        setInteractiveState(entry, true);
        visibleEntries.push(entry);
      });

      applyVisibleSpacing(visibleEntries);
      hidePagination();
      updateCount(visibleEntries.length, true, 1);

      if (emptyState) {
        emptyState.hidden = visibleEntries.length > 0;
      }

      if (clearButton) {
        clearButton.hidden = false;
      }
    };

    const queueRender = () => {
      window.cancelAnimationFrame(renderFrame);
      renderFrame = window.requestAnimationFrame(() => {
        if (normalize(activeQuery)) {
          renderFiltered();
          return;
        }

        renderPaginated();
      });
    };

    const syncToHash = (shouldScroll) => {
      if (normalize(activeQuery)) return;

      const targetEntry = findEntryByHash();

      if (targetEntry) {
        activePage = Math.ceil(targetEntry.order / pageSize);
        renderPaginated();

        if (shouldScroll) {
          window.requestAnimationFrame(() => {
            targetEntry.item.scrollIntoView({ block: 'start' });
          });
        }
        return;
      }

      const hashPage = getPageFromHash();

      if (hashPage) {
        activePage = hashPage;
        renderPaginated();
      }
    };

    if (form) {
      form.addEventListener('submit', (event) => {
        event.preventDefault();

        const firstVisible = entries.find((entry) => !entry.item.classList.contains('is-page-hidden') && !entry.item.classList.contains('is-filter-hidden'));

        if (firstVisible) {
          firstVisible.item.focus();
        }
      });
    }

    if (input) {
      input.addEventListener('input', () => {
        activeQuery = input.value || '';
        queueRender();
      });

      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && input.value) {
          input.value = '';
          activeQuery = '';
          queueRender();
          input.focus();
        }
      });
    }

    if (clearButton) {
      clearButton.addEventListener('click', () => {
        if (!input) return;

        input.value = '';
        activeQuery = '';
        queueRender();
        input.focus();
      });
    }

    if (pageSizeSelect) {
      pageSizeSelect.value = String(pageSize);
      pageSizeSelect.addEventListener('change', () => {
        const nextSize = Number(pageSizeSelect.value || pageSize);
        pageSize = PAGE_SIZE_OPTIONS.includes(nextSize) ? nextSize : pageSize;
        activePage = 1;
        queueRender();
      });
    }

    if (pageButtons) {
      pageButtons.addEventListener('click', (event) => {
        const button = event.target.closest('[data-page]');

        if (!(button instanceof HTMLButtonElement)) return;

        const nextPage = Number(button.dataset.page || activePage);

        if (Number.isNaN(nextPage) || nextPage === activePage) return;

        activePage = nextPage;
        renderPaginated();
        window.requestAnimationFrame(() => {
          section.scrollIntoView({ block: 'start' });
        });
      });
    }

    window.addEventListener('hashchange', () => {
      syncToHash(true);
    });

    syncToHash(false);
    queueRender();
  };

  const initAll = (root = document) => {
    if (!root) return;

    if (typeof root.matches === 'function' && root.matches(SECTION_SELECTOR)) {
      initSection(root);
    }

    if (typeof root.querySelectorAll === 'function') {
      root.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
